import { useMemo, useState } from "react";
import { GiBroadsword, GiCheckedShield, GiHeartPlus, GiSandsOfTime, GiUpgrade } from "react-icons/gi";
import { UnitCatalogEntry, UnitClassRu } from "../types/army";
import { ResourcePool } from "../types/castle";
import { formatDuration, formatNumber } from "../lib/format";
import { hapticImpact, hapticNotification } from "../lib/telegram";
import { CurrencyValue } from "./CurrencyIcon";
import { UnitIcon } from "./UnitIcon";
import {
  TIER_MAX,
  maxAllowedTier,
  maxBatchForBuildingLevel,
  requiredBuildingLevelForTier,
  tierCostMultiplier,
  tierName,
  tierStatMultiplier,
  tierTimeMultiplier,
} from "../lib/garrisonKey";

interface Props {
  unit: UnitCatalogEntry;
  // Tier the units currently sit at, and how many are available to promote.
  fromTier: number;
  available: number;
  resources: ResourcePool;
  trainSpeedReductionPct: number;
  // Level of the training building for this unit's class. Caps the reachable tier.
  buildingLevel: number;
  onClose: () => void;
  onConfirm: (count: number, toTier: number) => void;
  pending: boolean;
  error: string | null;
}

const presets = [10, 50, 100, 500];

function affordableMax(
  unit: UnitCatalogEntry,
  resources: ResourcePool,
  costMultDelta: number,
): number {
  const { costPerUnit } = unit;
  // Promotion charges only the tier delta; if the delta is zero-cost on a
  // resource, that resource never constrains the batch.
  const need = (v: number) => Math.ceil(v * costMultDelta);
  const cap = (have: number, perUnit: number) =>
    perUnit <= 0 ? Number.POSITIVE_INFINITY : Math.floor(have / perUnit);
  return Math.min(
    cap(resources.gold, need(costPerUnit.gold)),
    cap(resources.wood, need(costPerUnit.wood)),
    cap(resources.stone, need(costPerUnit.stone)),
    cap(resources.mana, need(costPerUnit.mana)),
  );
}

export function PromoteModal({
  unit,
  fromTier,
  available,
  resources,
  trainSpeedReductionPct,
  buildingLevel,
  onClose,
  onConfirm,
  pending,
  error,
}: Props) {
  const allowedTier = maxAllowedTier(buildingLevel);
  // Target tiers run from fromTier+1 up to the building-allowed max.
  const minTarget = fromTier + 1;
  const [toTier, setToTier] = useState<number>(Math.min(minTarget, Math.max(minTarget, allowedTier)));
  const effectiveTo = useMemo(
    () => Math.min(Math.max(minTarget, toTier), Math.max(minTarget, allowedTier)),
    [toTier, minTarget, allowedTier],
  );

  // Cost / time deltas between the target and the current tier.
  const costMultDelta = Math.max(0, tierCostMultiplier(effectiveTo) - tierCostMultiplier(fromTier));
  const timeMultDelta = Math.max(0, tierTimeMultiplier(effectiveTo) - tierTimeMultiplier(fromTier));
  const fromStat = tierStatMultiplier(fromTier);
  const toStat = tierStatMultiplier(effectiveTo);

  const maxAfford = affordableMax(unit, resources, costMultDelta);
  const maxBatch = maxBatchForBuildingLevel(buildingLevel);
  const maxAllowed = Math.max(0, Math.min(available, maxAfford, maxBatch));
  const [count, setCount] = useState<number>(() => Math.min(available, Math.max(1, maxAllowed)));

  const perUnitCost = {
    gold: Math.ceil(unit.costPerUnit.gold * costMultDelta),
    wood: Math.ceil(unit.costPerUnit.wood * costMultDelta),
    stone: Math.ceil(unit.costPerUnit.stone * costMultDelta),
    mana: Math.ceil(unit.costPerUnit.mana * costMultDelta),
  };
  const totalCost = {
    gold: perUnitCost.gold * count,
    wood: perUnitCost.wood * count,
    stone: perUnitCost.stone * count,
    mana: perUnitCost.mana * count,
  };
  const totalSeconds = Math.ceil(
    unit.timePerUnitSeconds * count * timeMultDelta * (1 - Math.min(trainSpeedReductionPct, 0.9)),
  );
  const canSubmit = count > 0 && count <= maxAllowed && effectiveTo > fromTier;

  const setSafe = (n: number) => {
    if (!Number.isFinite(n) || n <= 0) {
      setCount(0);
      return;
    }
    setCount(Math.min(Math.floor(n), Math.max(1, maxAllowed)));
  };

  const tierUnavailable = allowedTier <= fromTier;

  return (
    <div className="fixed inset-0 z-10 flex items-end justify-center bg-black/60" onClick={onClose}>
      <div className="w-full rounded-t-2xl bg-tg-bg p-4" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-tg-secondaryBg">
              <UnitIcon unitId={unit.id} cls={unit.cls} size={40} />
            </div>
            <div className="min-w-0">
              <div className="text-lg font-semibold leading-tight">{unit.name}</div>
              <div className="mt-0.5 text-xs text-tg-hint">
                {UnitClassRu[unit.cls]} · повышение из T{fromTier} · в наличии {formatNumber(available)}
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-tg-hint" aria-label="Закрыть">
            ✕
          </button>
        </div>

        {tierUnavailable ? (
          <div className="mb-3 rounded-lg bg-tg-secondaryBg p-3 text-sm text-tg-hint">
            Эти войска уже на максимальном тире для текущего уровня здания. Подними здание, чтобы открыть тир выше.
          </div>
        ) : (
          <>
            <div className="mb-2 flex items-center justify-between text-xs text-tg-hint">
              <span>Целевой тир</span>
              <span className="font-medium text-tg-text">
                T{fromTier} → {tierName(effectiveTo)} · T{effectiveTo}
              </span>
            </div>
            <div className="mb-3 flex flex-wrap gap-1">
              {Array.from({ length: TIER_MAX }, (_, i) => i + 1)
                .filter((t) => t > fromTier)
                .map((t) => {
                  const locked = t > allowedTier;
                  const selected = t === effectiveTo;
                  const reqLvl = requiredBuildingLevelForTier(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      disabled={locked}
                      onClick={() => {
                        hapticImpact("light");
                        setToTier(t);
                      }}
                      title={locked ? `Требуется здание Lvl ${reqLvl}` : `T${t} · ${tierName(t)}`}
                      className={
                        "rounded-md px-2 py-1 text-xs tabular-nums " +
                        (selected
                          ? "bg-tg-button text-tg-buttonText"
                          : locked
                          ? "bg-tg-secondaryBg text-tg-hint opacity-40"
                          : "bg-tg-secondaryBg text-tg-text")
                      }
                    >
                      T{t}
                    </button>
                  );
                })}
            </div>

            <div className="mb-3 text-xs text-tg-hint">
              <span className="inline-flex items-center gap-1">
                <GiHeartPlus size={12} /> {Math.round(unit.hp * fromStat)} → {Math.round(unit.hp * toStat)}
              </span>
              {" · "}
              <span className="inline-flex items-center gap-1">
                <GiBroadsword size={12} /> {Math.round(unit.attack * fromStat)} → {Math.round(unit.attack * toStat)}
              </span>
              {" · "}
              <span className="inline-flex items-center gap-1">
                <GiCheckedShield size={12} /> {Math.round(unit.defense * fromStat)} → {Math.round(unit.defense * toStat)}
              </span>
            </div>

            <div className="mb-3 flex flex-wrap gap-2">
              {presets.map((p) => (
                <button
                  key={p}
                  type="button"
                  disabled={p > maxAllowed}
                  onClick={() => {
                    hapticImpact("light");
                    setSafe(p);
                  }}
                  className="rounded-lg bg-tg-secondaryBg px-3 py-1 text-sm disabled:opacity-30"
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                disabled={maxAllowed <= 0}
                onClick={() => {
                  hapticImpact("light");
                  setSafe(maxAllowed);
                }}
                className="rounded-lg bg-tg-secondaryBg px-3 py-1 text-sm disabled:opacity-30"
              >
                Макс {maxAllowed > 0 ? `(${formatNumber(maxAllowed)})` : ""}
              </button>
            </div>

            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={maxAllowed || 1}
              value={count > 0 ? count : ""}
              placeholder={maxAllowed > 0 ? `до ${maxAllowed}` : "0"}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") {
                  setSafe(0);
                  return;
                }
                setSafe(parseInt(raw, 10));
              }}
              className="mb-3 w-full rounded-lg bg-tg-secondaryBg p-2 text-center text-lg tabular-nums"
            />

            <div className="mb-1 text-xs text-tg-hint">Доплата за тир:</div>
            <div className="mb-3 grid grid-cols-2 gap-1 text-sm text-tg-hint">
              <CurrencyValue kind="gold" value={formatNumber(totalCost.gold)} size={16} />
              <CurrencyValue kind="wood" value={formatNumber(totalCost.wood)} size={16} />
              <CurrencyValue kind="stone" value={formatNumber(totalCost.stone)} size={16} />
              <CurrencyValue kind="mana" value={formatNumber(totalCost.mana)} size={16} />
              <div className="col-span-2 pt-1 text-tg-text">
                <GiSandsOfTime className="mr-1 inline" size={14} /> {formatDuration(totalSeconds)}
              </div>
            </div>

            {error ? <div className="mb-2 text-sm text-tg-destructive">{error}</div> : null}

            <button
              type="button"
              disabled={!canSubmit || pending}
              onClick={() => {
                hapticImpact("medium");
                onConfirm(count, effectiveTo);
                hapticNotification("success");
              }}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-tg-button px-4 py-3 font-medium text-tg-buttonText disabled:opacity-40"
            >
              <GiUpgrade size={16} />
              {pending ? "Запускаю…" : `Повысить до T${effectiveTo}`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
